import React, { useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Mail, Clock, LifeBuoy, ArrowRight } from 'lucide-react';

const SUPPORT_EMAIL = 'info@museiq.one';

const SupportPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleEmail = () => {
    const subject = encodeURIComponent('MUSEIQ Support Request');
    const body = encodeURIComponent(
      'Hello MUSEIQ Support,\n\n' +
        'Please describe your issue below:\n\n' +
        '— App version:\n' +
        '— Device / OS:\n' +
        '— Account email:\n' +
        '— Description of the issue:\n\n' +
        'Thank you.'
    );
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main>
        <section className="pt-32 pb-16 bg-gradient-to-br from-white via-secondary/30 to-accent-blue/5">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-accent-blue/10 mb-6">
              <LifeBuoy className="w-8 h-8 text-accent-blue" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary mb-6 leading-tight">
              MUSEIQ Support
            </h1>
            <p className="text-xl text-body max-w-2xl mx-auto leading-relaxed">
              Need help with the MUSEIQ app or platform? Our team is here to
              assist you.
            </p>
          </div>
        </section>

        <section className="py-16">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8 md:p-12">
                <div className="flex flex-col md:flex-row md:items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 rounded-xl bg-accent-blue/10 flex items-center justify-center">
                      <Mail className="w-7 h-7 text-accent-blue" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-primary mb-3">
                      Contact us by email
                    </h2>
                    <p className="text-body mb-4 leading-relaxed">
                      For any questions, bug reports, account issues, or general
                      support inquiries, please email us at:
                    </p>
                    <a
                      href={`mailto:${SUPPORT_EMAIL}`}
                      className="inline-block text-2xl md:text-3xl font-semibold text-accent-blue hover:underline break-all"
                    >
                      {SUPPORT_EMAIL}
                    </a>
                    <div className="mt-6 flex flex-col sm:flex-row gap-3">
                      <Button
                        size="lg"
                        className="cta-button-neutral font-semibold px-6 py-3 rounded-xl"
                        onClick={handleEmail}
                      >
                        Email support
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-2 gap-6 mt-8">
              <Card className="border-0 shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Clock className="w-6 h-6 text-accent-blue flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-primary mb-2">
                        Response time
                      </h3>
                      <p className="text-body text-sm leading-relaxed">
                        We aim to respond to all support requests within 1–2
                        business days (Monday–Friday).
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <LifeBuoy className="w-6 h-6 text-accent-blue flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-primary mb-2">
                        What to include
                      </h3>
                      <p className="text-body text-sm leading-relaxed">
                        Please include your app version, device/OS, account
                        email, and a description of the issue.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="mt-12 text-center text-sm text-body/70">
              <p>
                MUSEIQ ApS · CVR 43572261 · Support email:{' '}
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="text-accent-blue hover:underline"
                >
                  {SUPPORT_EMAIL}
                </a>
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default SupportPage;
